import styled, { keyframes, css } from 'styled-components';
// ============================================
// ANIMATIONS
// ============================================
const pulse = keyframes `
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;
const slideInFromRight = keyframes `
  from { transform: translateX(20px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;
const fadeIn = keyframes `
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;
const shimmer = keyframes `
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;
// ============================================
// PAGE CONTAINER
// ============================================
export const DetailContainer = styled.div `
  width: 100%;
  min-height: calc(100vh - 120px);
  max-width: 1600px;
  margin: 0 auto;
  padding: 32px 32px 48px;
  box-sizing: border-box;
  overflow-x: hidden;
  animation: ${fadeIn} 0.4s ease-out;

  @media (max-width: 1400px) {
    max-width: 1400px;
    padding: 32px 24px 48px;
  }

  @media (max-width: 1024px) {
    max-width: 100%;
    padding: 24px 20px 32px;
  }

  @media (max-width: 768px) {
    padding: 24px 16px 32px;
  }

  @media (max-width: 375px) {
    padding: 16px 12px 24px;
  }
`;
export const BackLink = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 20px;
  transition: color 0.2s, gap 0.2s;

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.2s;
  }

  &:hover {
    color: var(--accent-gold, #e6c068);
    gap: 12px;

    svg {
      transform: translateX(-2px);
    }
  }
`;
// ============================================
// DETAIL GRID (2-column layout)
// ============================================
export const DetailGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 1024px) {
    gap: 20px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;
export const LeftColumn = styled.div `
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;
export const RightColumn = styled.div `
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: sticky;
  top: 90px;
  align-self: start;

  @media (max-width: 768px) {
    position: static;
    gap: 16px;
  }
`;
// ============================================
// DIAMOND IMAGE
// ============================================
export const DiamondImageContainer = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #e6c068), var(--accent-gold-light, #B8860B));
  }

  img {
    max-width: 220px;
    max-height: 220px;
    filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.2));
    transition: transform 0.4s ease;
  }

  &:hover img {
    transform: scale(1.05) rotate(2deg);
  }

  @media (max-width: 768px) {
    min-height: 200px;
    padding: 24px;

    img {
      max-width: 160px;
      max-height: 160px;
    }
  }
`;
// ============================================
// DIAMOND INFO CARD
// ============================================
export const DiamondInfoCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 24px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  h3 {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 16px 0;
  }

  @media (max-width: 768px) {
    padding: 16px;

    h3 {
      font-size: 18px;
    }
  }
`;
export const DiamondSpecsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
export const DiamondSpecItem = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 12px;
  text-align: center;

  .spec-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-tertiary);
    margin-bottom: 4px;
  }

  .spec-value {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
  }
`;
export const CertBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #fff;
  background: ${props => {
    switch (props.$lab?.toUpperCase()) {
        case 'GIA': return '#0056b3';
        case 'AGS': return '#1a1a1a';
        case 'IGI': return '#c41e3a';
        case 'HRD': return '#003366';
        default: return 'var(--accent-gold, #e6c068)';
    }
}};
  margin-top: 12px;
`;
// ============================================
// AUCTION INFO SECTION
// ============================================
export const AuctionInfoSection = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 24px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  h3 {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 10px;

    svg {
      color: var(--accent-gold, #e6c068);
      width: 18px;
      height: 18px;
    }
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;
export const AuctionInfoGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
export const AuctionInfoItem = styled.div `
  padding: 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);

  .info-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-tertiary);
    margin-bottom: 4px;
  }

  .info-value {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    display: flex;
    align-items: center;
    gap: 6px;

    svg {
      width: 14px;
      height: 14px;
      color: var(--accent-gold, #e6c068);
    }
  }
`;
// ============================================
// STATUS BADGE
// ============================================
const statusColors = {
    active: { bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', border: 'rgba(34, 197, 94, 0.3)' },
    grace_period: { bg: 'rgba(234, 179, 8, 0.1)', color: '#eab308', border: 'rgba(234, 179, 8, 0.3)' },
    scheduled: { bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: 'rgba(59, 130, 246, 0.3)' },
    closing: { bg: 'rgba(249, 115, 22, 0.1)', color: '#f97316', border: 'rgba(249, 115, 22, 0.3)' },
    closed: { bg: 'rgba(107, 114, 128, 0.1)', color: '#6b7280', border: 'rgba(107, 114, 128, 0.3)' },
    cancelled: { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'rgba(239, 68, 68, 0.3)' },
    draft: { bg: 'rgba(107, 114, 128, 0.1)', color: '#9ca3af', border: 'rgba(107, 114, 128, 0.3)' },
};
export const StatusBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => statusColors[props.$status]?.bg || statusColors.draft.bg};
  color: ${props => statusColors[props.$status]?.color || statusColors.draft.color};
  border: 1px solid ${props => statusColors[props.$status]?.border || statusColors.draft.border};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    ${props => (props.$status === 'active' || props.$status === 'grace_period') && css `
      animation: ${pulse} 1.5s ease-in-out infinite;
    `}
  }
`;
// ============================================
// BID PANEL
// ============================================
export const BidPanelContainer = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #e6c068), var(--accent-gold-light, #B8860B));
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;
export const CurrentBidDisplay = styled.div `
  text-align: center;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-color);

  .bid-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    color: var(--text-tertiary);
    margin-bottom: 6px;
  }

  .bid-amount {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 36px;
    font-weight: 700;
    color: var(--accent-gold, #e6c068);
    line-height: 1.2;
  }

  .bid-count {
    font-size: 12px;
    color: var(--text-secondary);
    margin-top: 6px;
  }

  @media (max-width: 768px) {
    .bid-amount {
      font-size: 28px;
    }
  }
`;
export const MinBidLabel = styled.div `
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 8px;

  strong {
    color: var(--text-primary);
    font-weight: 600;
  }
`;
export const BidInputRow = styled.div `
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;
export const BidInput = styled.input `
  flex: 1;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 14px 16px;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #e6c068);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(230, 192, 104, 0.1));
  }

  &::placeholder {
    color: var(--text-tertiary);
    font-weight: 400;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const BidButton = styled.button `
  background: linear-gradient(135deg, var(--accent-gold, #e6c068), var(--accent-gold-light, #B8860B));
  color: #fff;
  padding: 14px 28px;
  font-weight: 700;
  font-size: 14px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  min-width: 140px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(230, 192, 104, 0.35);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  ${props => props.$loading && css `
    pointer-events: none;
    opacity: 0.8;
  `}

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 480px) {
    min-width: 100px;
    padding: 14px 16px;
    font-size: 13px;
  }
`;
export const QuickBidButtons = styled.div `
  display: flex;
  gap: 8px;
  margin-bottom: 16px;

  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
`;
export const QuickBidButton = styled.button `
  flex: 1;
  padding: 10px 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-gold, #e6c068);
    color: var(--accent-gold, #e6c068);
    background: var(--accent-gold-bg, rgba(230, 192, 104, 0.06));
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
export const BidError = styled.div `
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.25);
  color: #ef4444;
  padding: 10px 14px;
  font-size: 13px;
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
export const BidSuccess = styled.div `
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(34, 197, 94, 0.25);
  color: #22c55e;
  padding: 10px 14px;
  font-size: 13px;
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
// ============================================
// BID HISTORY
// ============================================
export const BidHistoryContainer = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 24px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  h3 {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      color: var(--accent-gold, #e6c068);
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;
export const BidHistoryList = styled.div `
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: var(--bg-tertiary);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 2px;
  }
`;
export const BidHistoryItem = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: ${props => props.$isLatest ? 'var(--accent-gold-bg, rgba(230, 192, 104, 0.06))' : 'transparent'};
  border: 1px solid ${props => props.$isLatest ? 'var(--accent-gold-border, rgba(230, 192, 104, 0.15))' : 'transparent'};
  transition: background 0.2s;
  ${props => props.$isLatest && css `
    animation: ${slideInFromRight} 0.3s ease-out;
  `}

  &:hover {
    background: var(--bg-secondary);
  }

  .bid-bidder {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .bid-amount {
    font-size: 14px;
    font-weight: 700;
    color: var(--accent-gold, #e6c068);
  }

  .bid-time {
    font-size: 11px;
    color: var(--text-tertiary);
    min-width: 60px;
    text-align: right;
  }
`;
export const EmptyBids = styled.div `
  padding: 24px;
  text-align: center;
  color: var(--text-tertiary);
  font-size: 13px;
  border: 1px dashed var(--border-color);
`;
// ============================================
// TIMER DISPLAY
// ============================================
export const TimerDisplay = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 24px;
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  .timer-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    color: var(--text-tertiary);
    margin-bottom: 10px;
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;
export const TimerDigits = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;
export const TimerSegment = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 56px;

  .digit {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 32px;
    font-weight: 700;
    color: ${props => props.$urgent ? '#ef4444' : 'var(--text-primary)'};
    line-height: 1;
    ${props => props.$urgent && css `
      animation: ${pulse} 1s ease-in-out infinite;
    `}
  }

  .unit {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-tertiary);
    margin-top: 4px;
  }

  @media (max-width: 768px) {
    min-width: 44px;

    .digit {
      font-size: 24px;
    }
  }
`;
export const TimerSeparator = styled.span `
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.$urgent ? '#ef4444' : 'var(--text-tertiary)'};
  line-height: 1;
  padding: 0 2px;
  margin-bottom: 14px;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 12px;
  }
`;
export const GraceBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(234, 179, 8, 0.1);
  color: #eab308;
  border: 1px solid rgba(234, 179, 8, 0.3);
  margin-top: 12px;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;
export const AuctionEndedBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
  border: 1px solid rgba(107, 114, 128, 0.3);
  margin-top: 12px;
`;
// ============================================
// SUPERVISED STATUS BAR
// ============================================
const supervisedColors = {
    accepting_bids: { bg: 'rgba(34, 197, 94, 0.08)', color: '#22c55e', border: 'rgba(34, 197, 94, 0.3)' },
    going_once: { bg: 'rgba(234, 179, 8, 0.08)', color: '#eab308', border: 'rgba(234, 179, 8, 0.3)' },
    going_twice: { bg: 'rgba(249, 115, 22, 0.08)', color: '#f97316', border: 'rgba(249, 115, 22, 0.3)' },
    sold: { bg: 'rgba(239, 68, 68, 0.08)', color: '#ef4444', border: 'rgba(239, 68, 68, 0.3)' },
    paused: { bg: 'rgba(107, 114, 128, 0.08)', color: '#9ca3af', border: 'rgba(107, 114, 128, 0.3)' },
    closed: { bg: 'rgba(107, 114, 128, 0.08)', color: '#6b7280', border: 'rgba(107, 114, 128, 0.3)' },
    draft: { bg: 'rgba(107, 114, 128, 0.08)', color: '#9ca3af', border: 'rgba(107, 114, 128, 0.3)' },
};
export const SupervisedBanner = styled.div `
  width: calc(100% + 64px);
  margin-left: -32px;
  margin-right: -32px;
  padding: 16px 32px;
  background: ${props => supervisedColors[props.$state]?.bg || supervisedColors.draft.bg};
  border-top: 1px solid ${props => supervisedColors[props.$state]?.border || supervisedColors.draft.border};
  border-bottom: 1px solid ${props => supervisedColors[props.$state]?.border || supervisedColors.draft.border};
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;

  ${props => (props.$state === 'going_once' || props.$state === 'going_twice') && css `
    animation: ${pulse} 1.5s ease-in-out infinite;
  `}

  @media (max-width: 1400px) {
    width: calc(100% + 48px);
    margin-left: -24px;
    margin-right: -24px;
    padding: 16px 24px;
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    margin-bottom: 16px;
  }
`;
export const SupervisedBannerContent = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;
export const SupervisedStateText = styled.span `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 22px;
  font-weight: 700;
  color: ${props => supervisedColors[props.$state]?.color || supervisedColors.draft.color};
  text-transform: uppercase;
  letter-spacing: 2px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;
export const SupervisedProgressBar = styled.div `
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  margin-top: 10px;
  overflow: hidden;
  border-radius: 2px;
`;
export const SupervisedProgressFill = styled.div `
  height: 100%;
  width: ${props => props.$percent}%;
  background: ${props => supervisedColors[props.$state]?.color || '#9ca3af'};
  transition: width 1s linear;
  border-radius: 2px;
`;
// ============================================
// OUTBID ALERT TOAST
// ============================================
export const OutbidToast = styled.div `
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 10000;
  background: var(--bg-primary);
  border: 1px solid rgba(239, 68, 68, 0.4);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(239, 68, 68, 0.1);
  padding: 16px 20px;
  max-width: 380px;
  animation: ${slideInFromRight} 0.3s ease-out;

  .toast-title {
    color: #ef4444;
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .toast-message {
    color: var(--text-secondary);
    font-size: 13px;
    line-height: 1.5;
  }

  .toast-close {
    position: absolute;
    top: 8px;
    right: 8px;
    background: none;
    border: none;
    color: var(--text-tertiary);
    cursor: pointer;
    padding: 4px;
    font-size: 16px;
    line-height: 1;

    &:hover {
      color: var(--text-primary);
    }
  }
`;
// ============================================
// CONNECTION INDICATOR
// ============================================
export const ConnectionIndicator = styled.div `
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${props => props.$connected ? '#22c55e' : '#ef4444'};
  padding: 6px 10px;
  background: ${props => props.$connected ? 'rgba(34, 197, 94, 0.06)' : 'rgba(239, 68, 68, 0.06)'};
  border: 1px solid ${props => props.$connected ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    ${props => props.$connected && css `
      animation: ${pulse} 2s ease-in-out infinite;
    `}
  }
`;
// ============================================
// LOADING STATE
// ============================================
export const LoadingContainer = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  gap: 16px;
  color: var(--text-secondary);
  font-size: 14px;
`;
export const LoadingSkeleton = styled.div `
  background: linear-gradient(
    90deg,
    var(--bg-tertiary, #2a2a2a) 25%,
    var(--bg-secondary, #1f1f1f) 50%,
    var(--bg-tertiary, #2a2a2a) 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 4px;
  height: 20px;
  width: 100%;
`;
// ============================================
// HEADER ROW (back link + connection)
// ============================================
export const DetailHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;
